import { useState, useContext, useImperativeHandle } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import EditorInput from "../EditorInput";
import ConnectionOutput from "./components/ConnectionOutput";
import QueryOutput from "./components/QueryOutput";
import { testConnection, testQuery } from "api/UtilityServiceApis";
import { getCredentialById } from "api/CredentialManagementApi";
import { useVerticalResizer } from "hooks/useVerticalResizer";
import { EditorExpansion } from "../../../EditorExpansion";
import { useDebouncer } from "hooks/useDebouncer";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SqlButton from "./components/SqlButton";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";

const SqlEditorInput = ({ action, fieldValue, styles, allowEditWorkflow, ref }) => {
  const { user } = useAuth0();
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const isFlowActive = currentSavedFlow?.shared_status === "active";
  const { actionForm, componentAction, formErrors } = useContext(ComponentPropsContext);
  const { setActionForm, setFormErrors } = useContext(ComponentPropsContextApi);
  const [testConnectionOutput, setTestConnectionOutput] = useState(null);
  const [testQueryOutput, setTestQueryOutput] = useState(null);
  const [tabValue, setTabValue] = useState(1);
  const { startResizing, verticalHeight } = useVerticalResizer({
    defaultHeight: 300,
    minHeight: 200,
    maxHeight: 600
  });
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [editorValue, setEditorValue] = useState(fieldValue);
  const { debouncer } = useDebouncer(handleSqlEditorChange);

  const handleDebounceValue = (value) => {
    if (isFlowActive || !allowEditWorkflow) return;

    setEditorValue(value);
    debouncer(value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  function handleSqlEditorChange(value) {
    if (formErrors && formErrors.includes(action.field) && value !== "") {
      setFormErrors(formErrors.filter((error) => error !== action.field));
    }

    if (value === "") {
      setFormErrors((prev) => [...prev, action.field]);
    }

    const originalValue = value;
    const parsedSQLQuery = originalValue?.replace(/\\r\\n/g, "");

    setActionForm({
      ...actionForm,
      [action.field]: { ...actionForm[action.field], value: parsedSQLQuery }
    });
  }

  const validateFormInput = (requiredFields = []) => {
    const errors = [];

    requiredFields.forEach((field) => {
      if (!actionForm[field]?.value) {
        errors.push(field);
      }
    });

    return errors;
  };

  const getCredential = async () => {
    let credential = {};
    if (componentAction.isCredentialSupported) {
      try {
        const getCredentialByIdResponse = await getCredentialById(
          user?.stripeId,
          actionForm["credential"]?.value
        );
        credential = getCredentialByIdResponse?.resultObject?.application || {};
      } catch (error) {
        console.error("Error in getCredential: ", error);
      }
    }
    return credential;
  };

  const handleTestSqlConnection = async () => {
    if (isFlowActive || !allowEditWorkflow) return;

    try {
      let formEl = document.querySelector(".action-inputs");
      let requiredSqlConnectionFields = ["credential"];
      let lastChild = formEl.lastElementChild;

      let credential = await getCredential();

      const input = {
        input: {
          database: credential?.database,
          host: credential?.host,
          instanceName: null,
          password: credential?.password,
          port: parseInt(credential?.portNumber),
          username: credential?.username
        }
      };

      let validateFormInputResponse = validateFormInput(requiredSqlConnectionFields);
      if (validateFormInputResponse.length > 0) {
        setFormErrors(validateFormInputResponse);
        formEl.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const response = await testConnection(input);

      setTestConnectionOutput(response);
      if (lastChild) {
        setTabValue(1);
        lastChild.scrollIntoView({ block: "end", behavior: "smooth" });
      }

      console.log("Testing connection...");
    } catch (error) {
      console.error("Error in handleTestSqlConnection: ", error);
    }
  };

  const handleTestSqlQuery = async () => {
    if (isFlowActive || !allowEditWorkflow) return;

    try {
      let formEl = document.querySelector(".action-inputs");
      let lastChild = formEl.lastElementChild;
      const requiredSqlQueryFields = ["sqlStatement", "credential"];

      let credential = await getCredential();

      const input = {
        sqlStatement: actionForm["sqlStatement"]?.value,
        input: {
          database: credential?.database,
          host: credential?.host,
          instanceName: null,
          password: credential?.password,
          port: parseInt(credential?.portNumber),
          username: credential?.username
        }
      };

      let validateFormInputResponse = validateFormInput(requiredSqlQueryFields);
      if (validateFormInputResponse.length > 0) {
        setFormErrors(validateFormInputResponse);
        formEl.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const response = await testQuery(input);

      setTestQueryOutput(response);
      if (lastChild) {
        setTabValue(2);
        lastChild.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error in handleTestSqlQuery: ", error);
    }
  };

  useImperativeHandle(ref, () => ({
    triggerTestSqlConnection: handleTestSqlConnection,
    triggerTestSqlQuery: handleTestSqlQuery
  }));

  return (
    <SoftBox width="100%">
      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12 }}
          sx={{
            marginBottom: 1,
            position: "relative"
          }}
        >
          <SoftTypography variant="caption" fontSize="13px" fontWeight="bold" color="dark">
            {action.label}&nbsp;
            {action.isRequired && (
              <Box component="span" sx={styles.asterisk}>
                *
              </Box>
            )}
          </SoftTypography>
          {/* EditorInput with portal for fullscreen */}
          {!editorExpanded && (
            <SoftBox
              sx={{
                inset: "initial",
                position: "relative"
              }}
            >
              <EditorInput
                fieldValue={editorValue}
                language="sql"
                onChange={handleDebounceValue}
                options={{
                  wordWrap: "off",
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                    verticalScrollbarSize: 8
                  },
                  domReadOnly: isFlowActive || !allowEditWorkflow,
                  readOnly: isFlowActive || !allowEditWorkflow
                }}
                height={verticalHeight}
              />
              <EditorExpansion
                isExpanded={editorExpanded}
                handleExpansion={() => setEditorExpanded((prev) => !prev)}
              />
              <SoftBox className="app-horizontal-resizer shown" onMouseDown={startResizing} />
            </SoftBox>
          )}
          {editorExpanded &&
            createPortal(
              <SoftBox
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 99999,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(20, 20, 20, 1)",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <SoftBox sx={{ flex: 1, position: "relative" }}>
                  <EditorInput
                    fieldValue={editorValue}
                    language="sql"
                    onChange={handleDebounceValue}
                    options={{
                      wordWrap: "off",
                      scrollbar: {
                        vertical: "auto",
                        horizontal: "auto",
                        verticalScrollbarSize: 8
                      },
                      domReadOnly: isFlowActive || !allowEditWorkflow,
                      readOnly: isFlowActive || !allowEditWorkflow
                    }}
                    height="100%"
                  />
                  <SoftBox
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      zIndex: 100000
                    }}
                  >
                    <EditorExpansion
                      isExpanded={editorExpanded}
                      handleExpansion={() => setEditorExpanded((prev) => !prev)}
                    />
                  </SoftBox>
                </SoftBox>
              </SoftBox>,
              document.body
            )}
          {formErrors.includes(action.field) && (
            <FormHelperText error>{`${action.label} is required.`}</FormHelperText>
          )}
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            visibility: editorExpanded ? "hidden" : "visible",
            opacity: editorExpanded ? "0" : "1"
          }}
        >
          <SoftBox
            sx={{
              display: "inline-flex",
              gap: 1,
              mt: 1,
              mb: 1
            }}
          >
            {componentAction?.isCredentialSupported && (
              <SqlButton
                onClick={handleTestSqlConnection}
                icon={<SettingsEthernetIcon />}
                text="Test connection"
                disabled={isFlowActive || !allowEditWorkflow}
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px"
                }}
              />
            )}

            <SqlButton
              onClick={handleTestSqlQuery}
              icon={<PlayCircleOutlineIcon />}
              text="Test query"
              disabled={isFlowActive || !allowEditWorkflow}
              sx={{
                minWidth: "auto",
                padding: "4px 8px"
              }}
            />
          </SoftBox>
          <Tabs
            className="sql-editor"
            style={{ backgroundColor: "transparent" }}
            value={tabValue}
            onChange={handleTabChange}
          >
            <Tab value={1} label="Connection output" sx={{ fontSize: "12px" }} />
            <Tab value={2} label="Query output" sx={{ fontSize: "12px" }} />
          </Tabs>

          <SoftBox mt={2}>
            {tabValue === 1 && (
              <SoftBox>
                <ConnectionOutput data={testConnectionOutput} />
              </SoftBox>
            )}
            {tabValue === 2 && (
              <SoftBox>
                <QueryOutput data={testQueryOutput} />
              </SoftBox>
            )}
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
};

export default SqlEditorInput;
